import { keyBy } from 'lodash';
import {
  APIFieldAttributes,
  ArdoqId,
  ResourceType,
  WebSocketGlobalFieldDeletedPayload,
  WebSocketGlobalFieldRenamedPayload,
  WebSocketGlobalFieldUpdatedPayload,
} from '@ardoq/api-types';
import {
  ObservableState,
  reducedStream,
  reducer,
  spreadMap,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { filter, withLatestFrom } from 'rxjs/operators';
import { fetchAllReducer, websocketReducer } from 'streams/crud/reducers';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import {
  ArdoqEvent,
  isWebSocketGlobalFieldDeleted,
  isWebSocketGlobalFieldRenamed,
  isWebSocketGlobalFieldUpdated,
} from 'sync/types';
import * as fieldActions from './actions';
import { StreamCollection } from 'streams/utils/streamUtils';
import { fieldOps } from 'models/utils/fieldOps';
import {
  removeEntityById,
  resetState,
  returnPayload,
  SortAttribute,
  updateEntity,
} from '@ardoq/common-helpers';
import { localeCompareNumeric, Locale } from '@ardoq/locale';
import { toIds } from '@ardoq/core';

import { locale$ } from '../locale$';

export const toFieldTypeKey = (
  modelId: ArdoqId,
  typeId: string | number
): string => `${modelId}-${String(typeId)}`;

export type FieldsState = StreamCollection<APIFieldAttributes> & {
  byName: Map<string, APIFieldAttributes>;
  byComponentType: Map<string, APIFieldAttributes[]>;
  byReferenceType: Map<string, APIFieldAttributes[]>;
  fieldsById: Record<ArdoqId, APIFieldAttributes>;
  fieldsByModelIdAndTypeId: Record<string, Record<string, APIFieldAttributes>>;
};

const fieldComparator =
  (locale: Locale) => (a: APIFieldAttributes, b: APIFieldAttributes) => {
    const byOrder =
      (a[SortAttribute.ORDER] ?? 0) - (b[SortAttribute.ORDER] ?? 0);
    const aLabel = a.label || '';
    const bLabel = b.label || '';
    const byLabel = localeCompareNumeric(aLabel, bLabel, locale);
    return byOrder || byLabel;
  };

const sortFields = (
  fields: APIFieldAttributes[],
  locale: Locale
): APIFieldAttributes[] => {
  return fields.toSorted(fieldComparator(locale));
};

export const toFieldsState = (
  fields: APIFieldAttributes[],
  locale: Locale
): FieldsState => {
  const sortedFields = sortFields(fields, locale);
  const byName = new Map<string, APIFieldAttributes>();
  const byComponentType = new Map<string, APIFieldAttributes[]>();
  const byReferenceType = new Map<string, APIFieldAttributes[]>();
  const fieldsByModelIdAndTypeId: Record<
    string,
    Record<string, APIFieldAttributes>
  > = {};
  const fieldsById = keyBy(fields, '_id');

  for (const field of sortedFields) {
    const modelId = field.model;

    if (!byName.has(field.name)) {
      byName.set(field.name, field);
    }

    if (!fieldsByModelIdAndTypeId[modelId]) {
      fieldsByModelIdAndTypeId[modelId] = {};
    }

    for (const typeId of field.componentType) {
      const key = toFieldTypeKey(modelId, typeId);
      const existing = byComponentType.get(key) || [];
      byComponentType.set(key, [...existing, field]);

      fieldsByModelIdAndTypeId[modelId][String(typeId)] = field;
    }

    for (const typeId of field.referenceType) {
      const key = toFieldTypeKey(modelId, typeId);
      const existing = byReferenceType.get(key) || [];
      byReferenceType.set(key, [...existing, field]);

      fieldsByModelIdAndTypeId[modelId][String(typeId)] = field;
    }
  }

  return {
    byId: fieldsById,
    fieldsById,
    sortIds: toIds(sortedFields),
    models: sortedFields,
    byName,
    byComponentType,
    byReferenceType,
    fieldsByModelIdAndTypeId,
  };
};

const getFieldsStateInitialState = (): FieldsState => ({
  byId: {},
  fieldsById: {},
  sortIds: [],
  models: [],

  byName: new Map(),
  byComponentType: new Map(),
  byReferenceType: new Map(),
  fieldsByModelIdAndTypeId: {},
});

const addFields = (
  state: APIFieldAttributes[],
  { fields }: { fields: APIFieldAttributes[] }
): APIFieldAttributes[] => {
  const byId = new Map(state.map(f => [f._id, f]));
  fields.forEach(f => byId.set(f._id, f));
  return Array.from(byId.values());
};

const createField = (
  state: APIFieldAttributes[],
  field: APIFieldAttributes
): APIFieldAttributes[] => {
  if (state.some(f => f._id === field._id)) {
    return state;
  }
  return [...state, field];
};

const removeDeletedGlobalField = (
  state: APIFieldAttributes[],
  { data }: ArdoqEvent<WebSocketGlobalFieldDeletedPayload>
): APIFieldAttributes[] => state.filter(field => field.name !== data.name);

const applyUpdatedGlobalField = (
  state: APIFieldAttributes[],
  { data }: ArdoqEvent<WebSocketGlobalFieldUpdatedPayload>
): APIFieldAttributes[] =>
  state.map(field =>
    field.name === data.name
      ? fieldOps.withGlobalAttributes(field, data)
      : field
  );

const renameGlobalField = (
  state: APIFieldAttributes[],
  { data }: ArdoqEvent<WebSocketGlobalFieldRenamedPayload>
): APIFieldAttributes[] => {
  const renamedByOldName = new Map(
    fieldOps.toRenamedFields(data).map(renamed => [renamed.oldName, renamed])
  );
  return state.map(field => {
    const renamed = renamedByOldName.get(field.name);
    if (!renamed) return field;
    return { ...field, name: renamed.newName, label: renamed.label };
  });
};

const _fields$ = reducedStream<APIFieldAttributes[]>(
  '_fields$',
  [],
  [
    fetchAllReducer(returnPayload),
    reducer(fieldActions.resetFields, resetState([])),
    reducer(fieldActions.resetFieldsWithData, returnPayload),
    reducer(fieldActions.addFields, addFields),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIFieldAttributes>(ResourceType.FIELD)
      ),
      websocketReducer({
        create: createField,
        update: updateEntity,
        delete: removeEntityById,
      })
    ),
    streamReducer(
      websocket$.pipe(filter(isWebSocketGlobalFieldDeleted)),
      removeDeletedGlobalField
    ),
    streamReducer(
      websocket$.pipe(filter(isWebSocketGlobalFieldUpdated)),
      applyUpdatedGlobalField
    ),
    streamReducer(
      websocket$.pipe(filter(isWebSocketGlobalFieldRenamed)),
      renameGlobalField
    ),
  ],
  { namespace: fieldActions.fieldNamespace }
);

export const fields$: ObservableState<FieldsState> = toPersistentStream(
  'fields$',
  _fields$.pipe(withLatestFrom(locale$), spreadMap(toFieldsState)),
  getFieldsStateInitialState()
);
